import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Fab,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Update as UpdateIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import bookingService from '../../services/bookingService';
import api from '../../services/api';
import BookingDetailModal from './BookingDetailModal';
import ManualBookingDialog from './ManualBookingDialog';
import BookingEditModal from '../../components/BookingEditModal';
import MOTExpiryWidget from '../../components/MOTExpiryWidget';

const BookingList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const isAdmin = ['admin', 'org_admin', 'garage_manager'].includes(user?.role) || user?.is_superuser;

  // State management
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [manualBookingDialogOpen, setManualBookingDialogOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [bookingToEdit, setBookingToEdit] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load bookings
  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1, // API uses 1-based pagination
        page_size: rowsPerPage,
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await bookingService.getBookings(params);
      setBookings(response.results);
      setTotalCount(response.count);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load bookings on component mount and when filters change
  useEffect(() => {
    loadBookings();
  }, [page, rowsPerPage, searchTerm, statusFilter]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam && searchParam !== searchTerm) {
      setSearchTerm(searchParam);
    }
  }, [location.search]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) {
        loadBookings();
      } else {
        setPage(0); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleMenuClick = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBooking(null);
  };

  const handleViewDetails = () => {
    setSelectedBookingId(selectedBooking.id);
    setDetailModalOpen(true);
    handleMenuClose();
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedBookingId(null);
  };

  const handleOpenManualBooking = () => {
    setManualBookingDialogOpen(true);
  };

  const handleCloseManualBooking = () => {
    setManualBookingDialogOpen(false);
  };

  const handleManualBookingSuccess = (booking) => {
    // Reload bookings list
    loadBookings();
  };

  const handleEditBooking = () => {
    setBookingToEdit(selectedBooking);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setBookingToEdit(null);
  };

  const handleEditSuccess = (response) => {
    // Reload bookings list
    loadBookings();
  };

  const handleDeleteClick = () => {
    setBookingToDelete(selectedBooking);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleSendSMS = async () => {
    if (!selectedBooking?.customer?.phone) {
      setSnackbar({
        open: true,
        message: 'Customer has no phone number',
        severity: 'error',
      });
      handleMenuClose();
      return;
    }

    try {
      // Start conversation with customer's phone
      const response = await api.post('/v1/conversations/conversations/start_conversation/', {
        phone_number: selectedBooking.customer.phone,
        contact_name: selectedBooking.customer.name,
      });

      // Store conversation ID and navigate to chat
      sessionStorage.setItem('chat:openConversationId', response.data.conversation.id);
      handleMenuClose();
      navigate('/chat');
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error starting SMS conversation: ' + (err.response?.data?.error || err.message),
        severity: 'error',
      });
      handleMenuClose();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBookingToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!bookingToDelete) return;

    setDeleteLoading(true);
    try {
      await bookingService.deleteBooking(bookingToDelete.id);
      setSnackbar({
        open: true,
        message: `Booking for ${bookingToDelete.customer?.name || 'customer'} deleted successfully`,
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
      loadBookings();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to delete booking';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRowClick = (booking) => {
    setBookingToEdit(booking);
    setEditModalOpen(true);
  };

  // Helper functions
  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircleIcon color="success" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      case 'updated':
        return <UpdateIcon color="warning" />;
      default:
        return <EventIcon />;
    }
  };

  const getStatusChip = (booking) => {
    if (booking.is_cancelled) {
      return <Chip label="CANCELLED" color="error" size="small" />;
    }

    const colorMap = {
      'confirmed': 'success',
      'assigned': 'info',
      'in_progress': 'primary',
      'waiting_parts': 'error',
      'pending_admin_review': 'secondary',
      'updated': 'warning',
      'completed': 'default',
    };

    return (
      <Chip
        label={booking.status.toUpperCase()}
        color={colorMap[booking.status] || 'default'}
        size="small"
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Bookings
      </Typography>
      <Typography variant="body1" color="textSecondary" mb={3}>
        Manage BookMyGarage appointments and customer bookings
      </Typography>

      {/* MOT Expiry Widget */}
      {isAdmin && (
        <Box sx={{ mb: 3 }}>
          <MOTExpiryWidget limit={5} />
        </Box>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search customers, vehicles, registration..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="waiting_parts">Waiting Parts</MenuItem>
                  <MenuItem value="pending_admin_review">Pending Admin Review</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="updated">Updated</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Technician</TableCell>
                      <TableCell>Services</TableCell>
                      <TableCell>Cost</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow
                        key={booking.id}
                        hover
                        onClick={() => handleRowClick(booking)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getStatusIcon(booking.status)}
                            <Box ml={1}>
                              <Typography variant="body2" fontWeight="bold">
                                {formatDate(booking.booking_date)}
                              </Typography>
                              {booking.time_of_day && (
                                <Typography variant="caption" color="textSecondary">
                                  {booking.time_of_day}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {booking.customer.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {booking.customer.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {booking.vehicle.registration}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {booking.vehicle.make} {booking.vehicle.model}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(booking)}
                          {booking.location && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {booking.location}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {booking.assigned_technician ? (
                            <Typography variant="body2">
                              {booking.assigned_technician.name}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              Not assigned
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {booking.work_required && booking.work_required.length > 0 ? (
                            <Box>
                              {booking.work_required.slice(0, 2).map((work, index) => (
                                <Typography key={index} variant="caption" display="block">
                                  {work.service}
                                </Typography>
                              ))}
                              {booking.work_required.length > 2 && (
                                <Typography variant="caption" color="textSecondary">
                                  +{booking.work_required.length - 2} more
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              No services listed
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(booking.total_cost)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuClick(e, booking);
                            }}
                            size="small"
                          >
                            <MoreIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {bookings.length === 0 && !loading && (
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="textSecondary">
                    No bookings found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm || statusFilter
                      ? 'Try adjusting your search or filters'
                      : 'No bookings have been created yet'}
                  </Typography>
                </Box>
              )}

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditBooking}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        {selectedBooking?.customer?.phone && (
          <MenuItem onClick={handleSendSMS} sx={{ color: 'success.main' }}>
            <SmsIcon sx={{ mr: 1 }} />
            Send SMS
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Floating Action Buttons */}
      <Tooltip title="Create Manual Booking" placement="left">
        <Fab
          color="secondary"
          sx={{
            position: 'fixed',
            bottom: 96,
            right: 16,
          }}
          onClick={handleOpenManualBooking}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      <Tooltip title="Process Email" placement="left">
        <Fab
          color="primary"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
          }}
          onClick={() => navigate('/booking/email-processor')}
        >
          <EmailIcon />
        </Fab>
      </Tooltip>

      {/* Booking Detail Modal */}
      <BookingDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        bookingId={selectedBookingId}
      />

      {/* Manual Booking Dialog */}
      <ManualBookingDialog
        open={manualBookingDialogOpen}
        onClose={handleCloseManualBooking}
        onSuccess={handleManualBookingSuccess}
      />

      {/* Booking Edit Modal */}
      <BookingEditModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        booking={bookingToEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: 'error.main' }}>
          Delete Booking
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this booking?
            {bookingToDelete && (
              <Box component="span" sx={{ display: 'block', mt: 2, fontWeight: 'bold' }}>
                Customer: {bookingToDelete.customer?.name}<br />
                Vehicle: {bookingToDelete.vehicle?.registration} - {bookingToDelete.vehicle?.make} {bookingToDelete.vehicle?.model}
              </Box>
            )}
            <Box component="span" sx={{ display: 'block', mt: 2, color: 'warning.main' }}>
              This action cannot be undone. All related job requests and pending payments will also be deleted.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BookingList;
